import type { DataQuality } from "../../types";
import type { ResolvedWidgetValue } from "../../types";

export type QualityFooterStatus = "ok" | "warn" | "danger" | "muted" | "info";

export interface QualityPresentation {
  /** Texto principal do estado vazio (não-live). */
  title: string;
  /** Linha de apoio explicando o porquê. */
  meta: string;
  /** Tom do badge do rodapé. */
  footerStatus: QualityFooterStatus;
  /** Rótulo curto do badge. */
  footerLabel: string;
  /** Badge com ponto animado (vivo/aguardando). */
  animated: boolean;
}

/**
 * Fonte única de apresentação dos estados de qualidade (P0.2). Garante que
 * "aguardando telemetria" (device online) nunca se confunda com "offline"
 * nem com "dado desatualizado" — cada um tem texto, tom e rótulo próprios.
 */
export function describeQuality(quality: DataQuality): QualityPresentation {
  switch (quality) {
    case "waiting":
      return {
        title: "Aguardando telemetria",
        meta: "Dispositivo online",
        footerStatus: "info",
        footerLabel: "AGUARDANDO",
        animated: true,
      };
    case "stale":
      return {
        title: "Dado desatualizado",
        meta: "Última leitura recebida há mais tempo que o esperado",
        footerStatus: "warn",
        footerLabel: "DESATUALIZADO",
        animated: false,
      };
    case "offline":
      return {
        title: "Sem sinal",
        meta: "Dispositivo offline",
        footerStatus: "muted",
        footerLabel: "OFFLINE",
        animated: false,
      };
    case "live":
      return {
        title: "Ao vivo",
        meta: "Recebendo telemetria",
        footerStatus: "ok",
        footerLabel: "OK",
        animated: true,
      };
  }
}

/**
 * Qualidade efetiva de um valor resolvido. Usa o campo `quality` quando
 * presente (caminho real) e cai num fallback derivado de `isAvailable`
 * para construções sintéticas (preview do editor, acumulador) que não
 * setam o campo.
 */
export function effectiveQuality(value: ResolvedWidgetValue): DataQuality {
  return value.quality ?? (value.isAvailable ? "live" : "waiting");
}
